import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import LoadingOverlay from "react-loading-overlay";
import { MainContext } from "context/MainContext.js";
LoadingOverlay.propTypes = undefined;
import { ToastContainer, toast } from "react-toastify";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import { FaCheckCircle } from "react-icons/fa";

function BandwidthList() {
  const history = useHistory();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [errorMsg, setErrorMsg] = useState([]);

  const [params, setParams] = useState(location.state?.value ?? "");
  const [showLoading, setShowLoading] = useState(false);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const privileges = useContext(MainContext);

  const hideAlert = () => {
    setAlert(null);
  };

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["devices.bandwidths.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }

    const { state } = location;

    if (state?.successMsg) {
      successAlert(state.successMsg);
    } else if (state?.warningMsg) {
      warningAlert(state.warningMsg);
    }

    if (state?.successMsg || state?.warningMsg) {
      history.replace({
        ...location,
        state: { ...state, successMsg: undefined, warningMsg: undefined },
      });
    }
  }, []);

  const redirectToBandwidthEditPage = (id) => {
    const location = {
      pathname: "/admin/bandwidths/edit",
      state: {
        value: id,
        mode: id ? "edit" : "create",
      },
    };
    history.push(location);
  };

  const successAlert = (title, message) => {
    setAlert(
      <ReactBSAlert
        success
        style={{ display: "block", marginTop: "-100px" }}
        title={title}
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {message}
      </ReactBSAlert>,
    );
  };

  const warningAlert = (message) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Warning!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {message}
      </ReactBSAlert>,
    );
  };

  const deleteBandwidthConfirm = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure you want to delete this bandwidth ?"
        onConfirm={() => {
          deleteBandwidth(id);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      >
        You will not be able to recover this bandwidth
      </ReactBSAlert>,
    );
  };

  const deleteBandwidth = (id) => {
    setShowLoading(true);

    fetch("/api/v1/bandwidth/delete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        bandwidth_id: id,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        setShowLoading(false);
        var redirectToList = true;
        if (actualData.error_code) {
          redirectToList = false;
          setErrorMsg(actualData.error_code);
          toast.error("A problem occured while deleting the bandwidth", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        } else {
          setErrorMsg([]);
          toast.success("Bandwidth deleted successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
          setForceRefreshCount((prevValue) => ++prevValue);
        }
        hideAlert();
      })
      .catch((err) => {
        console.log(err.message);
        setShowLoading(false);
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        sortBy: "name",
        Cell: (props) => {
          return Utilities.validateLicense(
            sessionStorage.getItem("licenseTypes"),
            Utilities.LICENSE_PRO,
          ) && Utilities.hasAccess(privileges["devices.bandwidths.edit"]) ? (
            <a
              onClick={() => redirectToBandwidthEditPage(props.row.original.id)}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          ) : (
            <>{props.value}</>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Downstream (Mbps)",
        accessor: "downstream",
        sortBy: "downstream",
        flex: "1 1 0",
      },
      {
        Header: "Upstream (Mbps)",
        accessor: "upstream",
        sortBy: "upstream",
        flex: "1 1 0",
      },
      {
        Header: "Description",
        accessor: "description",
        sortBy: "description",
        flex: "1 1 0",
      },
      {
        Header: "Enforce",
        accessor: "enforce",
        sortBy: "enforce",
        flex: "1 1 0",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          const hasLicense = Utilities.validateLicense(
            sessionStorage.getItem("licenseTypes"),
            Utilities.LICENSE_PRO,
          );
          const canEdit =
            !props.row.original.deleted &&
            Utilities.hasAccess(privileges["devices.bandwidths.edit"]) &&
            hasLicense;
          const canDelete = Utilities.hasAccess(
            privileges["devices.bandwidths.delete"],
          );

          if (!canEdit && !canDelete) return "";

          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{ padding: "6px 14px 6px 14px" }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container={"body"}>
                {canEdit && (
                  <DropdownItem
                    onClick={() =>
                      redirectToBandwidthEditPage(props.row.original.id)
                    }
                  >
                    Edit Bandwidth
                  </DropdownItem>
                )}
                {canDelete && (
                  <DropdownItem
                    onClick={() =>
                      deleteBandwidthConfirm(props.row.original.id)
                    }
                  >
                    Delete Bandwidth
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />
      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        {alert}
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  {params.includes("serialNumber") ? (
                    <BreadcrumbItem>
                      <NavLink
                        to={{
                          pathname: "/admin/devices/list",
                        }}
                      >
                        GAM
                      </NavLink>
                      <i>&lt;{params.split("||")[1]}&gt;</i>
                    </BreadcrumbItem>
                  ) : (
                    ""
                  )}
                  <BreadcrumbItem>Bandwidths</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              {errorMsg.length > 0 ? (
                <div
                  style={{
                    backgroundColor: "#FFC0A4",
                    border: "1px solid #ef8157",
                    color: "#66615b",
                    paddingTop: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <ul>
                    {errorMsg.map((d, index) => (
                      <li key={index}>{d}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                ""
              )}
              <CardBody>
                <ReactTable
                  query="/api/v1/bandwidth/list"
                  title="Bandwidth Profiles"
                  columns={columns}
                  param={params}
                  forceRefresh={forceRefreshCount}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default BandwidthList;
